import { useLocation, useMatch } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CONSTANTS from '../../utils/constants/CONSTANTS';

import { setLocationFilter } from '../../redux/user/UserAction';
import { getLocationsAsync } from '../../redux/location/LocationAction';

import './index.scss';

const TopBar = () => {
  const { pathname } = useLocation();
  const dispatch = useDispatch();

  const pageFilters = useSelector((state) => state.user.pageFilters || {});
  const locations = useSelector((state) => state.locations.locations || []);
  const isLocationsLoading = useSelector((state) => state.clients.isLocationsLoading);
  const isSuperAdmin = useSelector((state) => state.user.currentUser.is_superuser);

  // Normalize the path for workout pages and dashboard
  const getNormalizedPath = (path) => {
    if (path.startsWith(CONSTANTS.URL_LIST.WORKOUT_BUILDER)) {
      return CONSTANTS.URL_LIST.WORKOUT_BUILDER;
    }
    if (path.startsWith(CONSTANTS.URL_LIST.DASHBOARD)) {
      return CONSTANTS.URL_LIST.DASHBOARD;
    }
    return path;
  };

  const normalizedPath = getNormalizedPath(pathname);

  const selectedLocation = pageFilters[normalizedPath];

  const pageChecks = useMemo(
    () => ({
      isLocationsListPage: pathname === CONSTANTS.URL_LIST.LOCATIONS,
      isSendPlanPage: pathname === CONSTANTS.URL_LIST.WORKOUT_BUILDER_SEND_PLAN,
      isSchoolListPage: pathname === CONSTANTS.URL_LIST.SCHOOL_LIST,
    }),
    [pathname]
  );

  /** if the user is on the Dashboard the top bar needs to be a little extended for styling and also
   * the background image needs to be changed, thus this class is needed */
  const extended = pathname === CONSTANTS.URL_LIST.DASHBOARD ? 'extend' : '';

  const hide = useMatch('/user/*') ? 'hide' : '';

  const [pageDetails, setPageDetails] = useState({
    heading: 'Dashboard',
    subHeading: 'My Dashboard',
  });

  const shouldShowLocationDropdown = useMemo(
    () =>
      isSuperAdmin &&
      pageDetails &&
      !pageChecks.isLocationsListPage &&
      !pageChecks.isSendPlanPage &&
      !pageChecks.isSchoolListPage,
    [isSuperAdmin, pageDetails, pageChecks]
  );

  const handleLocationChange = (e) => {
    if (!isSuperAdmin) return;
    const newLocation = e.target.value;
    // Use normalizedPath to ensure workout pages share the same filter
    dispatch(setLocationFilter(newLocation, normalizedPath));
  };

  // Set page details and initialize filter when path changes
  useEffect(() => {
    setPageDetails(
      pathname === CONSTANTS.URL_LIST.DASHBOARD
        ? {
            heading: CONSTANTS.URL_VALUES[pathname].heading,
            subHeading: isSuperAdmin
              ? CONSTANTS.URL_VALUES[pathname].superAdminSubHeading
              : CONSTANTS.URL_VALUES[pathname].subHeading,
          }
        : CONSTANTS.URL_VALUES[pathname]
    );

    // Only set filter if not already set and locations are loaded
    if (isSuperAdmin && !pageFilters[normalizedPath] && locations.length > 0) {
      // Check if location with default: true exists, otherwise fall back to first location
      const defaultLocation = locations.find((location) => location.default);
      const selectedLocationId = defaultLocation ? String(defaultLocation.id) : String(locations[0]?.id);
      if (selectedLocationId) {
        dispatch(setLocationFilter(selectedLocationId, normalizedPath));
      }
    }
  }, [pathname, pageFilters, locations]);

  // Fetch locations for admin users
  useEffect(() => {
    if (isSuperAdmin) {
      dispatch(getLocationsAsync());
    }
  }, [isSuperAdmin]);

  return (
    <div className={`top-bar ${extended} ${hide}`}>
      <div className="top-left">
        {pageDetails && (
          <div className="top-content">
            <div className="heading-group">
              <div className="heading">{pageDetails.heading}</div>
              <div className="sub-heading">{pageDetails.subHeading}</div>
            </div>
          </div>
        )}
      </div>
      <div className="top-right">
        {shouldShowLocationDropdown && (
          <div className="top-content">
            <p className="dropdown-label">Location</p>
            <div className="dropdown-separator" />
            <select
              className="location-dropdown"
              value={selectedLocation}
              onChange={handleLocationChange}
              disabled={isLocationsLoading}
            >
              {isLocationsLoading ? (
                <option className="location-option">Loading...</option>
              ) : (
                <>
                  {locations.map((location) => (
                    <option key={location.id} value={`${location.id}`} className="location-option">
                      {location.name}
                    </option>
                  ))}
                  {(pathname === CONSTANTS.URL_LIST.DASHBOARD || pathname === CONSTANTS.URL_LIST.COUPONCODES) && (
                    <option value="all" className="location-option">
                      All Locations
                    </option>
                  )}
                </>
              )}
            </select>
          </div>
        )}
      </div>
    </div>
  );
};

export default TopBar;
